import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import API from "@/api/API";
import Papa from "papaparse";
import { TEST_MANAUL_UPLOAD_PROJECTS } from "@/constants";

export const loadCamera = function () {
  const loader = new GLTFLoader();
  loader.setPath("/assets/model/");
  loader.load("camera.glb", (gltf) => {
    this.cameraObject = gltf.scene.children[0];
  });
};

export const createCameraInstancedMesh = function (instanceCount) {
  const cameraGeometry = this.cameraObject.geometry;
  const cameraMaterial = this.cameraObject.material;
  const instancedMesh = new THREE.InstancedMesh(
    cameraGeometry,
    cameraMaterial,
    instanceCount
  );
  instancedMesh.visible = false;

  this.scene.add(instancedMesh);

  return instancedMesh;
};

export const transformCamera = function (row) {
  const finalMatrix = new THREE.Matrix4();
  const positionMatrix = new THREE.Vector3();
  const quaternionMatrix = new THREE.Quaternion();

  const scaleMatrix = new THREE.Vector3(5, 5, 5);

  const relativePosition = new THREE.Vector3(row.x, row.y, row.alt);

  let defaultXRotation = Math.PI / 2;

  if (this.exportedManually) {
    defaultXRotation = -Math.PI / 2;
  }

  if (TEST_MANAUL_UPLOAD_PROJECTS.includes(this.projectId))
    defaultXRotation = 0;

  const rotationMatrix = new THREE.Matrix4();
  rotationMatrix.makeRotationX(defaultXRotation);
  relativePosition.applyMatrix4(rotationMatrix);

  const modelMatrix = new THREE.Matrix4();
  modelMatrix.copy(this.modelObject.children[0].matrixWorld);

  positionMatrix.copy(relativePosition).applyMatrix4(modelMatrix);

  const pitchRadians = THREE.MathUtils.degToRad(row.pitch);
  const headingRadians = THREE.MathUtils.degToRad(row.heading);
  const rollRadians = THREE.MathUtils.degToRad(row.roll);

  const cameraEuler = new THREE.Euler(
    pitchRadians,
    rollRadians - 5,
    -headingRadians,
    "ZXY"
  );

  const cameraQuaternion = new THREE.Quaternion().setFromEuler(cameraEuler);

  const modelQuaternion = new THREE.Quaternion();
  this.modelObject.children[0].getWorldQuaternion(modelQuaternion);

  const xRotation = new THREE.Quaternion();
  xRotation.setFromAxisAngle(new THREE.Vector3(1, 0, 0), defaultXRotation);

  quaternionMatrix
    .copy(modelQuaternion)
    .multiply(xRotation)
    .multiply(cameraQuaternion);

  finalMatrix.compose(positionMatrix, quaternionMatrix, scaleMatrix);
  finalMatrix.multiply(this.cameraObject.matrix);

  return finalMatrix;
};

export const fetchCameras = async function () {
  if (this.cameraMesh) return;

  if (!this.cameraPositionsCsvUrl) {
    const fastFusionModel = await API.projects.getFastFusionModel(
      this.projectId
    );
    this.cameraPositionsCsvUrl = fastFusionModel?.data?.camPosUrl;
    if (!this.cameraPositionsCsvUrl) {
      this.csvAvailable = false;
      return;
    }
  }

  try {
    const response = await fetch(this.cameraPositionsCsvUrl);
    if (!response.ok) {
      this.csvAvailable = false;
      return;
    }

    const csvText = await response.text();
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      complete: (results) => {
        const filteredResults = results.data.filter(
          (row) =>
            row.x && row.y && row.alt && row.pitch && row.heading && row.roll
        );
        if (filteredResults.length === 0) {
          this.csvAvailable = false;
          return;
        }
        this.cameraMesh = this.createCameraInstancedMesh(
          filteredResults.length
        );

        filteredResults.forEach((row, i) => {
          const cameraMatrix = this.transformCamera(row);
          this.cameraMesh.setMatrixAt(i, cameraMatrix);
        });
        this.camerasCsv = filteredResults;

        this.cameraMesh.instanceMatrix.needsUpdate = true;
      },
    });
  } catch (error) {
    console.error("Error fetching or parsing CSV:", error);
  }
};

export const displayCameras = async function () {
  if (!this.cameraMesh) {
    await this.fetchCameras();
  }
  this.changeCamerasVisibility(true);
};

export const changeCamerasVisibility = function (visible) {
  this.cameraMesh.visible = visible;

  if (visible && this.cameraMesh) {
    this.cameraControls.dollyTo(120, true);
  }
};
